// Every tab of the portal shares one localStorage, including the Auth0 token cache. When another
// tab changes account, this tab must not keep showing the previous account while its next token
// belongs to the new one.
//
// The signal is the cached ID token, not the remembered organization: another tab remembers the
// organization it is switching to before it has signed in, and may never finish (the user can
// abandon the Auth0 login). Only an ID token for a different account proves the switch happened.

export interface DisplayedAccount {
  subject?: string;
  organization?: string;
}

export type CachedAccount =
  | { status: 'present'; account: DisplayedAccount }
  | { status: 'missing' }
  | { status: 'unreadable' };

export type AccountStorageChange =
  // Another tab signed in to a different person or organization: reload into it.
  | 'foreign-account'
  // Another tab cleared the session (a switch or sign-out in progress): wait for its outcome.
  | 'account-removed'
  // Another tab refreshed or re-established the displayed account.
  | 'displayed-account'
  | 'unrelated';

// auth0-spa-js stores the ID token (the signed-in account) under
// `@@auth0spajs@@::<clientId>::@@user@@` in localStorage (auth/auth0Cache). Only this
// portal's client id counts: a stale entry left by another or retired client id on the same
// origin must never be mistaken for the account this portal is signed in to.
export const buildIdTokenCacheKey = (clientId: string) => `@@auth0spajs@@::${clientId}::@@user@@`;

const readOptionalString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

const readCachedIdTokenAccount = (entry: string): DisplayedAccount | undefined => {
  try {
    const claims = JSON.parse(entry)?.decodedToken?.claims;
    if (typeof claims !== 'object' || claims === null) return undefined;

    const subject = readOptionalString(claims.sub);
    if (!subject) return undefined;

    return { subject, organization: readOptionalString(claims.org_id) };
  } catch {
    return undefined;
  }
};

export const isSameAccount = (left: DisplayedAccount, right: DisplayedAccount) =>
  left.subject === right.subject && left.organization === right.organization;

export const classifyAccountStorageChange = (
  event: Pick<StorageEvent, 'key' | 'newValue'>,
  displayed: DisplayedAccount,
  clientId: string
): AccountStorageChange => {
  // localStorage.clear() in another tab removed the whole session, including the ID token.
  if (event.key === null) return 'account-removed';
  if (!clientId || event.key !== buildIdTokenCacheKey(clientId)) return 'unrelated';
  if (event.newValue === null) return 'account-removed';

  const cachedAccount = readCachedIdTokenAccount(event.newValue);
  if (!cachedAccount) return 'foreign-account';

  return isSameAccount(cachedAccount, displayed) ? 'displayed-account' : 'foreign-account';
};

// The account the shared Auth0 cache currently holds for this client, which another tab may
// have changed.
export const readCachedAccount = (clientId: string): CachedAccount => {
  if (!clientId) return { status: 'missing' };

  try {
    const entry = window.localStorage.getItem(buildIdTokenCacheKey(clientId));
    if (entry === null) return { status: 'missing' };

    const account = readCachedIdTokenAccount(entry);
    return account ? { status: 'present', account } : { status: 'unreadable' };
  } catch {
    return { status: 'unreadable' };
  }
};

export const reloadPage = () => window.location.reload();
